import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import apiClient from '../Services/AuthService';
import { getUserProject } from '../Services/UserProjectService';
import { useFeedback } from '../Context/FeedbackContext';

const FeedbackButton: React.FC = () => {
    const location = useLocation();
    const { projectSectionContentId } = useFeedback();
    const [projectId, setProjectId] = useState<string>('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [feedback, setFeedback] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);

    // Fetch user's project on mount
    useEffect(() => {
        const fetchProject = async () => {
            try {
                const project = await getUserProject();
                if (project?.projectId) {
                    setProjectId(project.projectId);
                }
            } catch (err) {
                console.error('Error fetching project for feedback:', err);
            }
        };
        fetchProject();
    }, []);
    const [error, setError] = useState<string | null>(null);

    const handleOpenModal = () => {
        setIsModalOpen(true);
        setFeedback('');
        setSubmitSuccess(false);
        setError(null);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setFeedback('');
        setSubmitSuccess(false);
        setError(null);
    };

    const handleSubmit = async () => {
        if (!feedback.trim()) {
            setError('Por favor, escribe tu feedback');
            return;
        }

        if (!projectId) {
            setError('No se pudo obtener el proyecto. Inténtalo de nuevo.');
            return;
        }

        setIsSubmitting(true);
        setError(null);

        try {
            await apiClient.post('/quiz/feedback', {
                project_id: projectId,
                project_section_content_id: projectSectionContentId,
                component: location.pathname,
                feedback: feedback.trim()
            });

            setSubmitSuccess(true);
            setTimeout(() => {
                handleCloseModal();
            }, 1500);
        } catch (err: any) {
            console.error('Error sending feedback:', err);
            setError(err.response?.data?.error || 'Error al enviar el feedback');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            {/* Floating Button */}
            <button
                onClick={handleOpenModal}
                className="feedback-button"
                style={{
                    position: 'fixed',
                    top: '20px',
                    left: '20px',
                    width: '50px',
                    height: '50px',
                    borderRadius: '50%',
                    backgroundColor: 'var(--color-dark)',
                    color: 'white',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.2rem',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
                    zIndex: 1000,
                    transition: 'transform 0.2s, box-shadow 0.2s'
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.1)';
                    e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.3)';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.2)';
                }}
                title="Enviar feedback"
            >
                <i className="bi bi-chat-dots"></i>
            </button>

            {/* Modal Overlay */}
            {isModalOpen && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1001
                    }}
                    onClick={handleCloseModal}
                >
                    {/* Modal Content */}
                    <div
                        style={{
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            padding: '2rem',
                            width: '90%',
                            maxWidth: '500px',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 style={{ margin: 0, fontWeight: '500' }}>Enviar Feedback</h5>
                            <button
                                onClick={handleCloseModal}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    fontSize: '1.5rem',
                                    cursor: 'pointer',
                                    color: '#666'
                                }}
                            >
                                &times;
                            </button>
                        </div>

                        {submitSuccess ? (
                            <div className="text-center py-4">
                                <i className="bi bi-check-circle text-success" style={{ fontSize: '3rem' }}></i>
                                <p className="mt-3 mb-0">Feedback enviado correctamente</p>
                            </div>
                        ) : (
                            <>
                                <textarea
                                    className="form-control"
                                    rows={5}
                                    placeholder="Escribe tu feedback aquí..."
                                    value={feedback}
                                    onChange={(e) => setFeedback(e.target.value)}
                                    disabled={isSubmitting}
                                    style={{
                                        resize: 'none',
                                        backgroundColor: '#f5f5f5',
                                        border: '1px solid #ddd'
                                    }}
                                />

                                {error && (
                                    <div className="text-danger small mt-2">
                                        <i className="bi bi-exclamation-circle me-1"></i>
                                        {error}
                                    </div>
                                )}

                                <div className="d-flex justify-content-end gap-2 mt-3">
                                    <button
                                        className="btn btn-outline-secondary"
                                        onClick={handleCloseModal}
                                        disabled={isSubmitting}
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        className="btn btn-dark"
                                        onClick={handleSubmit}
                                        disabled={isSubmitting || !feedback.trim()}
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                Enviando...
                                            </>
                                        ) : (
                                            'Enviar'
                                        )}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default FeedbackButton;
